import { Carousel } from "@redotech/redo-web/carousel";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import classNames from "classnames";
import { memo, useMemo, useState } from "react";
import { useChatMessagesRef } from "../../escalation/chat-messages-context";
import type { AskRedoUIMessage } from "../../types";
import * as styles from "./present-image-options-card.module.css";

interface PresentImageOption {
  url: string;
  caption?: string | null;
  badge?: string | null;
  onClickMessage: string;
}

interface PresentImageOptionsInput {
  options?: PresentImageOption[];
  headerText?: string | null;
}

/**
 * Clickable carousel for the `present-image-options` tool. The agent
 * supplies the options in the tool *input* (not output — the tool's
 * output is empty), each carrying the exact user message to send when
 * the merchant picks it. Clicking sends that message back into the
 * chat, so the agent decides how the choice is interpreted.
 *
 * Selection highlight is computed from the conversation: an option is
 * "selected" if a later user turn already sent its `onClickMessage`.
 * We read the live messages ref (the same seam the escalate-to-human
 * card uses) so re-opening a thread shows the previously-picked option.
 */
export interface PresentImageOptionsCardProps {
  input: unknown;
  toolCallId: string;
  disabled: boolean;
  onSelect: (message: string) => void;
}

export const PresentImageOptionsCard = memo(function PresentImageOptionsCard({
  input,
  toolCallId,
  disabled,
  onSelect,
}: PresentImageOptionsCardProps) {
  const messagesRef = useChatMessagesRef();
  const args = input as PresentImageOptionsInput | undefined;

  const options = useMemo(() => {
    const raw = args?.options;
    return Array.isArray(raw) ? raw.filter((o) => !!o?.url) : [];
  }, [args]);

  const selectedClickMessage = findSelectedClickMessage(
    messagesRef.current,
    toolCallId,
    options,
  );

  if (options.length === 0) {
    return null;
  }

  return (
    <Flex dir="column" gap="sm" pb="md" w="full">
      {args?.headerText ? (
        <Text fontSize="sm" textColor="secondary">
          {args.headerText}
        </Text>
      ) : null}
      <Carousel gap="md" numberVisible={2.2} showVignette>
        {options.map((option) => (
          <ImageOptionThumbnail
            disabled={disabled}
            key={option.onClickMessage + option.url}
            onClick={() => onSelect(option.onClickMessage)}
            option={option}
            selected={option.onClickMessage === selectedClickMessage}
          />
        ))}
      </Carousel>
    </Flex>
  );
});

interface ImageOptionThumbnailProps {
  option: PresentImageOption;
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
}

function ImageOptionThumbnail({
  option,
  selected,
  disabled,
  onClick,
}: ImageOptionThumbnailProps) {
  const [errored, setErrored] = useState(false);
  const imageProps = {
    alt: "",
    // Block the browser's native image drag — otherwise dragging on a
    // thumbnail starts a ghost-image drag and hijacks the carousel's
    // horizontal pan, so the merchant can't swipe between options.
    draggable: false,
    onError: () => setErrored(true),
    src: option.url,
  };

  return (
    <Flex dir="column" gap="xxs" w="full">
      <Flex
        aria-label={option.caption || option.onClickMessage}
        aria-pressed={selected}
        as="button"
        className={classNames(styles.thumbnailButton, {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          [styles.selected!]: selected,
        })}
        disabled={disabled}
        onClick={onClick}
        p="none"
        w="full"
      >
        {errored ? (
          <Flex className={styles.thumbnailFallback} height="full" w="full" />
        ) : (
          <Flex
            {...imageProps}
            as="img"
            className={styles.thumbnailImage}
            height="full"
            w="full"
          />
        )}
        {option.badge ? (
          <Text
            as="span"
            className={styles.badge}
            fontSize="xxs"
            fontWeight="semibold"
            px="xs"
            py="xxs"
            textColor="primary"
          >
            {option.badge}
          </Text>
        ) : null}
        {selected ? (
          <Flex
            align="center"
            aria-hidden
            as="span"
            className={styles.selectedOverlay}
            justify="center"
            w="xl"
          >
            <Text fontSize="xxs" fontWeight="bold" textColor="white">
              ✓
            </Text>
          </Flex>
        ) : null}
      </Flex>
      {option.caption ? (
        <Text
          as="span"
          fontSize="xs"
          mt="xxs"
          overflow="hidden"
          textColor="secondary"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {option.caption}
        </Text>
      ) : null}
    </Flex>
  );
}

/**
 * The selected option is the most recent user turn (after the tool
 * call) whose text matches one of the options' `onClickMessage`
 * values. Returns undefined when the merchant hasn't picked yet.
 */
export function findSelectedClickMessage(
  messages: AskRedoUIMessage[],
  toolCallId: string,
  options: PresentImageOption[],
): string | undefined {
  if (options.length === 0) {
    return undefined;
  }
  const toolCallMessageIndex = messages.findIndex((message) =>
    message.parts.some(
      (part) => part.type === "dynamic-tool" && part.toolCallId === toolCallId,
    ),
  );
  if (toolCallMessageIndex < 0) {
    return undefined;
  }
  const knownClickMessages = new Set(options.map((o) => o.onClickMessage));
  let latestMatch: string | undefined;
  for (let i = toolCallMessageIndex + 1; i < messages.length; i++) {
    const message = messages[i];
    if (!message || message.role !== "user") {
      continue;
    }
    const userText = extractUserText(message).trim();
    if (userText && knownClickMessages.has(userText)) {
      latestMatch = userText;
    }
  }
  return latestMatch;
}

function extractUserText(message: AskRedoUIMessage): string {
  return message.parts
    .filter(
      (part): part is { type: "text"; text: string } => part.type === "text",
    )
    .map((part) => part.text)
    .join("");
}
